import { POLLY_LANGUAGE_OPTIONS } from '../../../data/pollyVoices';
import { localizedTextFieldTestId, type LocalizedText } from '../../../utils/localizedSsml';
import { hasIssueForTestId, type ValidationIssue } from '../../../utils/validationIssues';

import { TranslateFromJapaneseButton } from './TranslateFromJapaneseButton';

export function LocalizedTextFields({
  label,
  languages,
  value,
  defaults = {},
  defaultsAreValues = true,
  onChange,
  sourceText,
  translateTestId,
  issues = [],
}: {
  label: string;
  languages: readonly string[];
  value?: LocalizedText;
  /**
   * 言語別の既定文面。
   *
   * `defaultsAreValues` が `false` のときは **placeholder (薄字) 専用** になり、
   * 入力欄の値としては使われない (Issue #556)。
   */
  defaults?: LocalizedText;
  /**
   * 既定文面を「値」として扱うか (Issue #556)。
   *
   * ## なぜ切り替えるのか
   *
   * 遅延放送 (自列車 / 接続線) の SSML は「空欄 = 定型文マスタへ委譲」が正しい
   * 保存規約 (Issue #527)。ところが言語別文面だけは、放送言語のチェックを入れた
   * 時点で **コード内既定訳が値として自動投入** されていた。個別文面はマスタより
   * 優先されるため、
   *
   *   定型文マスタで英中韓を整備しても、該当言語を有効にした瞬間に無効化される
   *
   * という状態になっていた (Issue #556 / 日本語以外の反映率 0%)。
   *
   * そこで遅延放送では `false` を渡し、既定文面を **薄字 (placeholder)** として
   * 提示するだけに留める。空欄のまま保存すれば放送側がマスタへフォールバックする。
   *
   * 任意テキスト (`free_text`) は定型文マスタを持たず、文面の出どころが他に無いため
   * 既定値の概念自体が無い (既定 `true` のまま呼び出し側は `defaults` を渡さない)。
   */
  defaultsAreValues?: boolean;
  onChange: (value: LocalizedText) => void;
  /**
   * このコンテンツに紐づく入力エラー (Issue #545)。
   *
   * 言語別文面が未入力のまま保存しようとした場合に、該当言語の欄へ赤枠
   * (`.is-invalid`) を付けて「どの言語を埋めればよいか」を示すために使う。
   */
  issues?: ValidationIssue[];
  /**
   * 「日本語から翻訳」ボタンの翻訳元となる日本語の文面 (Issue #492 / SBI-11)。
   *
   * 呼び出し側が持つ日本語入力欄の値 (`props.text` / `ssml_template` など) を渡す。
   * 未指定 / 空文字の場合はボタンが無効化され、「日本語の文面を入力すると翻訳できます」
   * とヒントを出す (翻訳元が無いのにボタンだけ押せる状態にしない)。
   */
  sourceText?: string;
  /**
   * 翻訳ボタンの `data-testid` を一意にするための接尾辞 (Issue #492)。
   *
   * 1 つのモーダルに複数の多言語入力欄 (パターン行 × 接続線 × 方面) が並ぶため、
   * 未指定だと E2E / テストが目的の欄のボタンを特定できない。省略時は `label` を
   * そのまま使う (`label` は既に欄ごとに一意な文言になっている)。
   */
  translateTestId?: string;
}): React.JSX.Element | null {
  /*
   * Issue #556: 既定文面を「値」として混ぜるかどうかを切り替える。
   *
   * `defaultsAreValues === false` (遅延放送) では既定文面を混ぜない。
   * 混ぜると `onChange` が既定訳を含む辞書を保存し、個別文面として焼き込まれてしまう
   * (= 定型文マスタの多言語編集が反映されなくなる)。
   */
  const localized = defaultsAreValues ? { ...defaults, ...value } : { ...value };
  const selectedLanguages = POLLY_LANGUAGE_OPTIONS.filter(
    (language) => language.key !== 'ja' && languages.includes(language.key),
  );
  if (selectedLanguages.length === 0) return null;
  return (
    <details open style={{ margin: '8px 0', minWidth: 0 }}>
      <summary>各言語の翻訳</summary>
      {/*
        Issue #492 (SBI-11): 入力欄の **上部** に「日本語から翻訳」ボタンを置く。
        押すと日本語の文面をもとに各言語欄へ訳文を下書きする。

        ⚠️ 翻訳結果は **保存されない**。ここで行うのは `onChange` によるフォーム状態の
        更新だけで、保存はモーダルの「保存」ボタン (人手修正後の明示操作) で行う。
        機械翻訳をそのまま放送すると誤案内に直結するため、この前提を崩さないこと。
      */}
      <TranslateFromJapaneseButton
        sourceText={sourceText ?? ''}
        targetLanguages={selectedLanguages.map((language) => language.key)}
        value={localized}
        onChange={onChange}
        testId={translateTestId ?? label}
      />
      {selectedLanguages.map((language) => {
        const testId = localizedTextFieldTestId(translateTestId ?? label, language.key);
        return (
          <label key={language.key} style={{ display: 'block', marginTop: 8 }}>
            <span>{language.label}</span>
            <textarea
              rows={2}
              aria-label={`${label} ${language.label}`}
              // Issue #545: 未入力の言語欄を赤枠で示し、エラーサマリからスクロールできるようにする。
              data-testid={testId}
              className={hasIssueForTestId(issues, testId) ? 'is-invalid' : ''}
              style={{ width: '100%', boxSizing: 'border-box' }}
              value={localized[language.key] ?? ''}
              /*
                Issue #556: 未入力時に実際に放送される文面を薄字で提示する。
                遅延放送 (`defaultsAreValues === false`) では
                「定型文設定 (当該言語) → コード内既定」で解決した文面が入るため、
                空欄のままにすると **定型文設定の文面がそのまま放送される** ことが分かる。
              */
              placeholder={defaultsAreValues ? undefined : defaults[language.key]}
              onChange={(event) => onChange({ ...localized, [language.key]: event.target.value })}
            />
          </label>
        );
      })}
    </details>
  );
}
